(function () {
  'use strict';

  if (customElements.get('discovery-product-list')) return;

  class DiscoveryProductList extends HTMLElement {
    connectedCallback() {
      if (this._abortController) return;
      this.track = this.querySelector('[data-discovery-product-list-track]');
      this.previousButton = this.querySelector('[data-discovery-product-list-previous]');
      this.nextButton = this.querySelector('[data-discovery-product-list-next]');
      this.autoplayButton = this.querySelector('[data-discovery-product-list-autoplay]');
      this.progress = this.querySelector('[data-discovery-product-list-progress]');
      if (!this.track || !this.previousButton || !this.nextButton) return;

      this._abortController = new AbortController();
      this._manuallyPaused = false;
      this._interactionPaused = false;
      this._scrollFrame = null;
      this._drawer = this.closest('discovery-drawer');
      this._block = this.closest('[data-discovery-block]');
      this._panel = this.closest('[data-discovery-panel]');
      var signal = this._abortController.signal;

      this.previousButton.addEventListener('click', () => this.move(-1), { signal: signal });
      this.nextButton.addEventListener('click', () => this.move(1), { signal: signal });
      this.track.addEventListener('scroll', () => this.requestControlUpdate(), { passive: true, signal: signal });
      this.querySelectorAll('.discovery-product-list__card').forEach((card) => {
        card.addEventListener('pointerenter', () => this.setInteractionPaused(true), { signal: signal });
        card.addEventListener('pointerleave', () => this.setInteractionPaused(false), { signal: signal });
        card.addEventListener('focusin', () => this.setInteractionPaused(true), { signal: signal });
        card.addEventListener('focusout', (event) => {
          if (!event.relatedTarget || !card.contains(event.relatedTarget)) this.setInteractionPaused(false);
        }, { signal: signal });
      });
      document.addEventListener('visibilitychange', () => this.syncAutoplay(), { signal: signal });
      window.addEventListener('resize', () => this.requestControlUpdate(), { passive: true, signal: signal });

      if (this.progress) {
        this.progress.addEventListener('animationiteration', () => {
          if (this.dataset.autoplayPaused !== 'true') this.move(1, true);
        }, { signal: signal });
      }

      if (this._drawer && window.MutationObserver) {
        this._visibilityObserver = new MutationObserver(() => {
          this.requestControlUpdate();
          this.syncAutoplay();
        });
        this._visibilityObserver.observe(this._drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
        if (this._block) this._visibilityObserver.observe(this._block, { attributes: true, attributeFilter: ['hidden'] });
        if (this._panel) this._visibilityObserver.observe(this._panel, { attributes: true, attributeFilter: ['hidden', 'inert'] });
      }

      if (this.autoplayButton) {
        this.autoplayButton.addEventListener('click', () => {
          this._manuallyPaused = !this._manuallyPaused;
          this.updateAutoplayButton();
          this.syncAutoplay();
        }, { signal: signal });
      }

      this.updateControls();
      this.updateAutoplayButton();
      this.syncAutoplay();
    }

    disconnectedCallback() {
      if (this._abortController) this._abortController.abort();
      this._abortController = null;
      if (this._visibilityObserver) this._visibilityObserver.disconnect();
      this._visibilityObserver = null;
      if (this._scrollFrame) cancelAnimationFrame(this._scrollFrame);
      this._scrollFrame = null;
    }

    prefersReducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    isActive() {
      return Boolean(
        (!this._drawer || this._drawer.getAttribute('aria-hidden') === 'false') &&
        !this.closest('[hidden]') &&
        !this.closest('[inert]') &&
        this.getClientRects().length
      );
    }

    pageWidth() {
      return Math.max(1, this.track.clientWidth);
    }

    move(direction, automatic) {
      var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      var atEnd = this.track.scrollLeft >= maxScroll - 2;
      var target = automatic && direction > 0 && atEnd ? 0 : this.track.scrollLeft + direction * this.pageWidth();
      this.track.scrollTo({ left: Math.max(0, Math.min(maxScroll, target)), behavior: this.prefersReducedMotion() ? 'auto' : 'smooth' });
      if (!automatic) this.restartAutoplay();
    }

    requestControlUpdate() {
      if (this._scrollFrame) return;
      this._scrollFrame = requestAnimationFrame(() => {
        this._scrollFrame = null;
        this.updateControls();
      });
    }

    updateControls() {
      var maxScroll = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.previousButton.disabled = this.track.scrollLeft <= 2;
      this.nextButton.disabled = this.track.scrollLeft >= maxScroll - 2;
      var hasOverflow = maxScroll > 2;
      this.previousButton.hidden = !hasOverflow;
      this.nextButton.hidden = !hasOverflow;
      if (this.autoplayButton) this.autoplayButton.hidden = !hasOverflow || this.prefersReducedMotion();
      if (!hasOverflow) this.dataset.autoplayPaused = 'true';
    }

    setInteractionPaused(paused) {
      this._interactionPaused = paused;
      this.syncAutoplay();
    }

    updateAutoplayButton() {
      if (!this.autoplayButton) return;
      this.autoplayButton.setAttribute('aria-pressed', this._manuallyPaused ? 'true' : 'false');
      this.autoplayButton.setAttribute('aria-label', this._manuallyPaused ? this.autoplayButton.dataset.playLabel : this.autoplayButton.dataset.pauseLabel);
    }

    restartAutoplay() {
      if (this.progress) {
        this.progress.style.animation = 'none';
        this.progress.getBoundingClientRect();
        this.progress.style.removeProperty('animation');
      }
      this.syncAutoplay();
    }

    syncAutoplay() {
      var shouldPause = this.dataset.autoplay !== 'true' ||
        !this.isActive() ||
        this._manuallyPaused ||
        this._interactionPaused ||
        document.hidden ||
        this.prefersReducedMotion() ||
        this.track.scrollWidth <= this.track.clientWidth + 2;
      this.dataset.autoplayPaused = shouldPause ? 'true' : 'false';
    }
  }

  customElements.define('discovery-product-list', DiscoveryProductList);
})();
